/**
 * FERP Portal - Global Search Palette (Cmd+K)
 *
 * Three-tier command palette with navigation, entity search, and history:
 * - Tier 1: Navigation (pages/routes) - instant client-side filter
 * - Tier 2: Entity search - debounced GraphQL cross-subgraph search
 * - Tier 3: Recent searches - localStorage history (shown when query empty)
 *
 * FERP Principles:
 * - Data-driven: All nav items from SubgraphMetadata
 * - Repository pattern: Search via GraphQL
 * - Dumb frontend: Results from backend
 * - Mantine v8.x only (Spotlight)
 */

import { Spotlight, spotlight } from '@mantine/spotlight';
import { useHotkeys } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconHistory, IconX, IconPlus } from '@tabler/icons-react';
import { useTenantSubgraphConfig } from '@/hooks/useTenantSubgraphConfig';
import { useEntitySearch } from '@/hooks/useEntitySearch';
import { useAuth } from '@/auth/AuthContext';
import { SearchResultCard } from './SearchResultCard';
import { getIcon } from '@/utils/iconMap';
import {
  getSearchHistory,
  addToSearchHistory,
  clearSearchHistory,
} from '@/utils/searchHistory';
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';

export function GlobalSearchPalette() {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const { config, loading: configLoading } = useTenantSubgraphConfig(tenant?.id ?? null);
  const { query, setQuery, results, totalCount, loading: searchLoading } = useEntitySearch();
  const [historyRefresh, setHistoryRefresh] = useState(0);

  // Register Cmd+K / Ctrl+K hotkey
  useHotkeys([['mod+K', () => spotlight.open()]]);

  if (configLoading || !config) {
    return null;
  }

  // Quick Actions (always available at top)
  const quickActions = [
    {
      id: 'quick-add-task',
      label: 'Quick Add Task',
      description: 'Create a new task quickly (⌘.)',
      onClick: () => {
        window.dispatchEvent(new CustomEvent('ferp:quick-add-task'));
        spotlight.close();
      },
      leftSection: <IconPlus size={20} />,
    },
  ];

  // Tier 1: Navigation actions from SubgraphMetadata
  const navActions = config.allNavItems
    .filter((item) => item.path) // Only nav items with paths
    .map((item) => {
      const IconComponent = getIcon(item.icon);
      return {
        id: item.id,
        label: item.label,
        description: `Navigate to ${item.label}`,
        onClick: () => {
          navigate(item.path);
          spotlight.close();
        },
        leftSection: <IconComponent size={20} />,
      };
    });

  // Tier 2: Entity search results (async)
  const entityActions = results.map((result) => {
    const subgraphInfo = config.subgraphs.find((s) => s.subgraphId === result.subgraphId);
    return {
      id: result.id,
      label: result.displayValue,
      description: `${result.entityType} in ${subgraphInfo?.displayName ?? result.subgraphId}`,
      onClick: () => {
        // Save to history when user clicks a result
        addToSearchHistory({
          query,
          timestamp: new Date().toISOString(),
          resultCount: totalCount,
        });

        if (result.detailPath) {
          navigate(result.detailPath);
        } else {
          // Fallback: navigate to subgraph browser with entity filter
          navigate(`/admin/${result.subgraphId}?entity=${result.entityType}`);
        }
        spotlight.close();
      },
      renderAction: () => <SearchResultCard result={result} subgraphInfo={subgraphInfo} />,
    };
  });

  // Tier 3: Recent search history (shown when query is empty)
  const history = getSearchHistory();
  const historyActions =
    query.length === 0 && history.length > 0
      ? [
          // Clear history button
          {
            id: 'clear-history',
            label: 'Clear search history',
            description: 'Remove all recent searches',
            onClick: () => {
              clearSearchHistory();
              setHistoryRefresh((prev) => prev + 1); // Trigger re-render
            },
            leftSection: <IconX size={20} />,
          },
          // History items
          ...history.map((item) => ({
            id: `history-${item.query}`,
            label: item.query,
            description: `${item.resultCount} results • ${formatDistanceToNow(
              new Date(item.timestamp)
            )} ago`,
            onClick: () => {
              setQuery(item.query);
            },
            leftSection: <IconHistory size={20} />,
          })),
        ]
      : [];

  return (
    <Spotlight
      // @ts-expect-error Type assignment
      actions={[...quickActions, ...historyActions, ...navActions, ...entityActions]}
      searchProps={{
        leftSection: <IconSearch size={20} />,
        placeholder: 'Search pages and entities... (Cmd+K)',
        value: query,
        onChange: setQuery,
      }}
      nothingFound={
        searchLoading
          ? 'Searching...'
          : query.length === 0 && history.length === 0
          ? 'No recent searches'
          : 'No results found'
      }
      highlightQuery
      limit={10}
      // @ts-expect-error Type assignment
      shortcut="mod+K"
    />
  );
}

export default GlobalSearchPalette;
